import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API } from '@/utils/API';

interface UseSubmissionStateOptions {
  submissionId: string | undefined;
  onSave: () => Promise<void>;
  initialStatus?: string;
  onSuccess?: () => void;
}

/**
 * Hook to manage submission state including submission status and form submission logic
 */
export const useSubmissionState = ({
  submissionId,
  onSave,
  initialStatus,
  onSuccess,
}: UseSubmissionStateOptions) => {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(
    initialStatus !== undefined && initialStatus !== 'draft'
  );

  // Update submitted status when initial status changes
  useEffect(() => {
    setIsSubmitted(initialStatus !== undefined && initialStatus !== 'draft');
  }, [initialStatus]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!submissionId) {
      setSubmitError('No submission ID provided');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Save current state first
      await onSave();

      // Submit the form
      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/transitions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transition: { event: 'submit' } }),
        }
      );

      if (!response.ok) {
        let errorMessage = 'Unable to submit. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // body wasn't JSON — keep the generic message
        }
        throw new Error(errorMessage);
      }

      setIsSubmitted(true);
      onSuccess?.();

      // Invalidate cache to refresh tables and individual submission data
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
    } catch (err) {
      // TypeErrors from fetch() indicate a network-level failure (no connection,
      // DNS failure, request blocked). The browser message varies by browser
      // ("Failed to fetch", "Load failed", "NetworkError", etc.) so we catch
      // the whole TypeError subclass rather than matching specific strings.
      const message =
        err instanceof TypeError
          ? 'Unable to reach the server. Please check your connection and try again.'
          : err instanceof Error
            ? err.message
            : typeof err === 'string'
              ? err
              : 'Unable to submit. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [submissionId, onSave, onSuccess, queryClient]);

  return {
    isSubmitted,
    isSubmitting,
    submitError,
    handleSubmit,
  };
};
